import CallCenterIcon from "@components/svg/callCenterIcon";
import {
    formatPhoneNumber,
    getPhoneNumber,
    renderQrText,
} from "@lib/sharedUtils";
import { useDomainContext } from "@hooks/useDomainContext";
import { callEvent } from "src/api";
import styles from "./styles.module.scss";
import { CSSProperties, ReactNode, useEffect } from "react";
import PhoneIcon from "@components/severalbrands/partnerWithUsPage/formSection/icons/phoneIcon";
import QrCodeImage from "../QrCodeImage";

export default function ProxiedNumber({
    slogan,
    iconColor,
    textColor,
    emptyIcon,
    phoneNumberColor,
    stickyStyle,
    hideSticky,
    icon,
    phoneNumberClassName,
    iconPath,
}: {
    slogan?: string;
    iconColor?: string;
    textColor?: string;
    emptyIcon?: JSX.Element;
    mobileMessage?: string;
    phoneNumberColor?: string;
    stickyStyle?: {
        stickTitleColor?: string;
        stickBtnTxtColor?: string;
        stickBtnBgColor?: string;
        stickBtnBorderColor?: string;
        stickyPulseColor?: string;
        maxWidth?: string;
    };
    hideSticky?: boolean;
    icon?: ReactNode;
    phoneNumberClassName?: string;
    iconPath?: string;
}) {
    const {
        category,
        headerOffer,
        stickyOffer,
        domain,
        visitDetails,
        routeConfig,
        loadingOffers,
        ignoreSticky,
        buyerPhoneNumber,
        domainNameTranslation,
    } = useDomainContext();

    const qrTextBlock = domain?.textBlocks.find(
        (txtBlock) => txtBlock.position === "qr-code-sticky-message",
    );

    const isDesktop = visitDetails?.platform === "desktop";

    const callUsStickyTitle =
        domain?.config?.callUsStickyTitle ||
        category?.config?.callUsStickyTitle ||
        domain?.translations?.[
            `${domainNameTranslation}_CALL_US_STICKY_TITLE`
        ] ||
        "Call Us";

    const domainSlogan = (
        routeConfig?.config?.settings?.slogan || domain.slogan
    )?.replace(/\[s\]/gi, visitDetails?.region ?? "");

    let phoneNumber: string | undefined;
    // Prioritize buyer phone number from API call over offer phone numbers
    if (buyerPhoneNumber) {
        phoneNumber = buyerPhoneNumber;
    } else if (category && headerOffer?.length) {
        phoneNumber = getPhoneNumber(headerOffer[0]);
    } else {
        phoneNumber = "";
    }

    const stickyPhoneNumber = buyerPhoneNumber
        ? buyerPhoneNumber
        : stickyOffer?.length
        ? getPhoneNumber(stickyOffer[0])
        : "";

    const qrText = renderQrText(qrTextBlock?.text, stickyPhoneNumber);

    let stickyOfferType = "";
    let stickyTitle = "";
    let stickyLink = "";
    let stickyButtonLabel = "";

    /* Sticky mobile config from variant config */
    const stickyTitleFromVariantConfig =
        routeConfig?.templateConfig?.variantConfig?.stickyMobileConfig?.label;
    const buttonLabelFromVariantConfig =
        routeConfig?.templateConfig?.variantConfig?.stickyMobileConfig
            ?.buttonLabel;
    const buttonIconFromVariantConfig =
        routeConfig?.templateConfig?.variantConfig?.stickyMobileConfig?.icon;
    /* Sticky mobile config variant config*/

    if (category && stickyOffer?.length && !hideSticky) {
        const stickyPhoneNumberMessage =
            category.config?.stickyPhoneNumberMessage;
        if (stickyPhoneNumber) {
            // number Offer
            stickyOfferType = "numberOffer";
            stickyTitle = stickyOffer[0].attributes?.phoneNumberMessage
                ? stickyOffer[0].attributes?.phoneNumberMessage
                : stickyOffer[0].hiddenAttributes?.phoneNumberMessage
                ? stickyOffer[0].hiddenAttributes?.phoneNumberMessage
                : stickyPhoneNumberMessage
                ? stickyPhoneNumberMessage
                : "Call Now to Get the Best Deal";
            stickyLink = `tel:${stickyPhoneNumber}`;
        } else {
            // link Offer
            stickyOfferType = "linkOffer";
            stickyTitle = stickyOffer[0].attributes?.stickyOfferMessage
                ? stickyOffer[0].attributes?.stickyOfferMessage
                : "";
            stickyLink = `/offers/${stickyOffer[0].slug}`;
            stickyButtonLabel = stickyOffer[0].attributes?.stickyOfferBtnLabel
                ? stickyOffer[0].attributes?.stickyOfferBtnLabel
                : "";
        }
    }

    useEffect(() => {
        const banner = document.getElementById("stickyOfferBanner");
        const claimsSection = document.getElementById("section-0");

        if (ignoreSticky && banner && claimsSection) {
            banner.classList.add("hidden");

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        // Hero section is in view → user is at the top
                        banner.classList.add("hidden");
                    } else {
                        // Hero section is out of view → user scrolled down
                        banner.classList.remove("hidden");
                    }
                },
                { threshold: 0.65 },
            );

            observer.observe(claimsSection);
        }
    }, []);

    // Show offers when: page offers are not hidden OR buyer has phone number override
    const shouldShowOffers =
        !routeConfig?.config?.settings?.hidePageOffers || !!buyerPhoneNumber;

    const shouldShowSticky =
        stickyOffer?.length && !hideSticky && shouldShowOffers;

    return (
        <>
            {shouldShowSticky ? (
                isDesktop && qrText ? (
                    <div
                        className={`${styles["stickyQR"]} ${
                            ignoreSticky ? "hidden" : ""
                        }`}
                        id="stickyOfferBanner"
                    >
                        <div className={`${styles["qrCodeContainer"]}`}>
                            <QrCodeImage
                                phoneNumber={stickyPhoneNumber}
                                height="100px"
                                width="100px"
                            />
                        </div>

                        <div className={`${styles["qrText"]}`}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: qrText ?? "",
                                }}
                                onClick={(e) => {
                                    const target = e.target as HTMLElement;

                                    if (
                                        target.tagName === "A" &&
                                        target.dataset.phone
                                    ) {
                                        void callEvent({
                                            event: "Call",
                                            phoneNumber: target.dataset.phone,
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <a
                        href={`${stickyLink}`}
                        onClick={() => {
                            if (stickyOfferType === "numberOffer") {
                                void callEvent({
                                    event: "Call",
                                    phoneNumber: stickyPhoneNumber as string,
                                });
                            }
                        }}
                        style={
                            {
                                "--text-color": stickyStyle?.stickTitleColor,
                                "--button-text-color":
                                    stickyStyle?.stickBtnTxtColor ??
                                    domain.config?.stickyButtonTextColor,
                                "--button-bg-color":
                                    stickyStyle?.stickBtnBgColor ??
                                    domain.config?.stickyButtonBgColor,
                                "--button-border-color":
                                    stickyStyle?.stickBtnBorderColor,
                                "--button-pulse-color":
                                    stickyStyle?.stickyPulseColor ??
                                    domain.config?.stickyPulseColor ??
                                    "191, 10, 47",
                                "--max-width": stickyStyle?.maxWidth ?? "80rem",
                            } as CSSProperties
                        }
                        className={`${styles["sticky"]} rtlStyle ${
                            ignoreSticky ? "hidden" : ""
                        }  items-center`}
                        id="stickyOfferBanner"
                    >
                        {stickyTitleFromVariantConfig ? (
                            <h3>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: `${stickyTitleFromVariantConfig}`,
                                    }}
                                />
                            </h3>
                        ) : (
                            stickyTitle && (
                                <h3>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `${stickyTitle}`,
                                        }}
                                    />
                                </h3>
                            )
                        )}
                        <div
                            className={`flex items-center ${styles["buttonStyle"]} ltrStyle`}
                            style={{ minWidth: "142px" }}
                        >
                            <span className="text-sm lg:text-lg font-bold md:font-normal flex gap-[2px] md:gap-2 items-center">
                                <span
                                    className={`${styles["desktopIcon"]} ${styles["phoneIcon"]}  h-[20px] md:h-8 w-[20px] md:w-8`}
                                >
                                    <PhoneIcon color="currentColor" />
                                </span>
                                <span
                                    className={`${styles["mobileIcon"]}   h-[20px] md:h-8 w-[20px] md:w-8 relative`}
                                >
                                    {buttonIconFromVariantConfig ? (
                                        <img
                                            src={buttonIconFromVariantConfig}
                                            alt={`Mobile Call Icon`}
                                        />
                                    ) : (
                                        <PhoneIcon color="currentColor" />
                                    )}
                                </span>

                                <span className={styles.number}>
                                    {stickyOfferType === "numberOffer"
                                        ? formatPhoneNumber(stickyPhoneNumber)
                                        : stickyButtonLabel}
                                </span>
                                <span className={styles["numberMobile"]}>
                                    {buttonLabelFromVariantConfig
                                        ? buttonLabelFromVariantConfig
                                        : stickyOfferType === "numberOffer"
                                        ? formatPhoneNumber(stickyPhoneNumber)
                                        : stickyButtonLabel}
                                </span>

                                <span
                                    className={`hidden md:inline text-lg font-normal ${styles["call-us"]}`}
                                >
                                    {callUsStickyTitle}
                                </span>
                            </span>
                        </div>
                    </a>
                )
            ) : (
                <></>
            )}
            {phoneNumber && shouldShowOffers ? (
                <div
                    className={`${styles["phoneNumber"]} ${
                        phoneNumberClassName ?? ""
                    }`}
                >
                    <a
                        className={`flex items-center `}
                        href={`tel:${phoneNumber}`}
                        style={
                            {
                                "--text-color": phoneNumberColor ?? textColor,
                                "--icon-color": iconColor ?? textColor,
                            } as CSSProperties
                        }
                        onClick={() => {
                            void callEvent({
                                event: "Call",
                                phoneNumber: phoneNumber as string,
                            });
                        }}
                    >
                        {iconPath ? (
                            <img
                                src={iconPath}
                                className="w-7 h-7 lg:w-9 lg:h-9 mr-2"
                                alt={`Call Icon`}
                            />
                        ) : (
                            icon ?? (
                                <CallCenterIcon className="w-7 h-7 lg:w-9 lg:h-9 mr-2" />
                            )
                        )}
                        <div className={`text-sm lg:text-lg font-bold `}>
                            {loadingOffers ? (
                                <div
                                    className={styles.loading}
                                    key={phoneNumber}
                                />
                            ) : (
                                <div
                                    className={styles.animatePhoneNumber}
                                    key={phoneNumber}
                                >
                                    {formatPhoneNumber(phoneNumber)}
                                </div>
                            )}
                        </div>
                    </a>
                    {headerOffer &&
                    (headerOffer[0]?.hiddenAttributes?.phoneNumberMessage ||
                        headerOffer[0]?.attributes?.phoneNumberMessage) &&
                    headerOffer[0]?.hiddenAttributes?.showHeaderPhoneNumber ? (
                        <div className={styles.phoneNumberMessage}>
                            <span
                                style={{
                                    fontSize: "11px",
                                    color:
                                        phoneNumberColor ?? textColor ?? "#000",
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${
                                        headerOffer[0].hiddenAttributes
                                            ?.phoneNumberMessage ??
                                        headerOffer[0].attributes
                                            ?.phoneNumberMessage
                                    } `,
                                }}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            ) : (
                <div
                    className="hidden lg:flex items-center"
                    style={
                        {
                            "--text-color": textColor ?? phoneNumberColor,
                        } as CSSProperties
                    }
                >
                    {emptyIcon}

                    <span className={`${styles.slogan} ml-3 `}>
                        {domainSlogan ? (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `${domainSlogan} `,
                                }}
                            />
                        ) : slogan ? (
                            <span>
                                <span>{slogan[0].toUpperCase()}</span>
                                <span className="lowercase">
                                    {slogan.slice(1)}
                                </span>
                            </span>
                        ) : (
                            ""
                        )}
                    </span>
                </div>
            )}
        </>
    );
}
